import React, { useState } from 'react';
import { complianceService } from '../../services/api';
import { getAppMarket } from '../../services/MarketService';

interface CertificateUploadStepProps {
    propertyId: string;
    onFinish: () => void;
}

// F-307: US document set for the Texas market — up to 5 slots, each labelled
// with its required/optional status (human directive: "we are the digital
// vault"). The UK market keeps its gas/electrical/EPC onboarding set.
const UK_CERT_TYPES = [
    { id: 'GAS_SAFETY', label: 'Gas Safety Certificate (CP12)', icon: 'fa-fire-flame-simple', required: true },
    { id: 'EICR', label: 'Electrical Installation Condition Report (EICR)', icon: 'fa-bolt', required: true },
    { id: 'EPC', label: 'Energy Performance Certificate (EPC)', icon: 'fa-house-circle-check', required: true },
    { id: 'PAT', label: 'Portable Appliance Testing (PAT)', icon: 'fa-plug', required: false },
    { id: 'LEGIONELLA', label: 'Legionella Risk Assessment', icon: 'fa-droplet', required: false },
];

const US_CERT_TYPES = [
    { id: 'LEASE_AGREEMENT', label: 'Texas Lease Agreement', icon: 'fa-file-contract', required: true },
    { id: 'LEAD_PAINT_DISCLOSURE', label: 'Lead Paint Disclosure (pre-1978 homes)', icon: 'fa-paint-roller', required: true },
    { id: 'REGISTRATION_CERTIFICATE', label: 'Municipal Registration Certificate', icon: 'fa-clipboard-check', required: true },
    { id: 'HVAC_SERVICE', label: 'HVAC / A/C Service Record', icon: 'fa-snowflake', required: true },
    { id: 'CONDITION_REPORT', label: 'Property Condition Report', icon: 'fa-house-circle-check', required: false },
];

export default function CertificateUploadStepV2({ propertyId, onFinish }: CertificateUploadStepProps) {
    const CERT_TYPES = getAppMarket() === 'texas' ? US_CERT_TYPES : UK_CERT_TYPES;
    const [currentStep, setCurrentStep] = useState(0); // Which certificate we're uploading
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [expiryDate, setExpiryDate] = useState('');
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [uploadedCerts, setUploadedCerts] = useState<string[]>([]);

    const currentCert = CERT_TYPES[currentStep];
    const isLastCert = currentStep === CERT_TYPES.length - 1;
    const allCertsUploaded = uploadedCerts.length === CERT_TYPES.length;

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            setError('');
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setError('Please select a file first');
            return;
        }

        if (!expiryDate) {
            setError('Please enter the expiry date');
            return;
        }

        // 2026-03-09: Allow expired documents during onboarding, but flag them
        const expiry = new Date(expiryDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const isExpired = expiry < today;

        setUploading(true);
        setError('');

        try {
            // Presigned S3 flow — the legacy multipart POST goes through
            // API Gateway, which (a) caps the body at 10 MB and (b) base64-
            // encodes binary bodies, so multer was seeing garbled bytes and
            // silently dropping uploads. Going direct to S3 is the working
            // path and matches how the rest of the app uploads certs.
            const presign = await complianceService.getPresignedUploadUrl({
                fileName: selectedFile.name,
                fileType: selectedFile.type || 'application/octet-stream',
                propertyId,
            });
            const { uploadUrl, s3Key } = presign.data?.data || {};
            if (!uploadUrl || !s3Key) {
                throw new Error('Could not obtain upload URL');
            }

            const putRes = await fetch(uploadUrl, {
                method: 'PUT',
                headers: { 'Content-Type': selectedFile.type || 'application/octet-stream' },
                body: selectedFile,
            });
            if (!putRes.ok) {
                throw new Error(`S3 upload failed (${putRes.status})`);
            }

            await complianceService.confirmCertificateUpload({
                name: currentCert.label,
                type: currentCert.id,
                propertyId,
                issuedDate: new Date().toISOString().split('T')[0],
                expiryDate,
                s3Key,
                fileName: selectedFile.name,
                fileSize: selectedFile.size,
                uploadTimestamp: new Date().toISOString(),
                isExpired,
            });

            setUploadedCerts(prev => [...prev, currentCert.id]);
            setSelectedFile(null);
            setExpiryDate('');

            if (isLastCert) {
                setTimeout(() => onFinish(), 1000);
            } else {
                setCurrentStep(prev => prev + 1);
            }
        } catch (err: any) {
            console.error('Upload failed', err);
            setError(err?.response?.data?.message || err?.message || 'Upload failed. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const handleSkip = () => {
        // Reset fields and move to next
        setSelectedFile(null);
        setExpiryDate('');
        setError('');

        if (isLastCert) {
            onFinish();
        } else {
            setCurrentStep(prev => prev + 1);
        }
    };

    if (allCertsUploaded) {
        return (
            <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-100">
                <div className="p-12 text-center">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-4">
                        <i className="fas fa-check text-green-600 text-3xl"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">All Certificates Uploaded!</h3>
                    <p className="text-gray-600 mb-6">You've successfully uploaded all compliance certificates.</p>
                    <button
                        onClick={onFinish}
                        className="bg-emerald-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-emerald-700 transition-colors"
                    >
                        Go to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-100">
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-5">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-xl font-bold text-white">Upload Certificates</h3>
                        <p className="text-emerald-100 text-sm mt-1">
                            Certificate {currentStep + 1} of {CERT_TYPES.length}
                        </p>
                    </div>
                    <div className="text-white text-sm">
                        <i className="fas fa-upload mr-2"></i>
                        {uploadedCerts.length} uploaded
                    </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-4 bg-emerald-700 rounded-full h-2">
                    <div
                        className="bg-white rounded-full h-2 transition-all duration-500"
                        style={{ width: `${(uploadedCerts.length / CERT_TYPES.length) * 100}%` }}
                    ></div>
                </div>
            </div>

            {/* Content */}
            <div className="p-8">
                {/* Current Certificate */}
                <div className="mb-6">
                    <div className="flex items-center mb-4">
                        <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-emerald-100 text-emerald-600 mr-4">
                            <i className={`fas ${currentCert.icon} text-xl`}></i>
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h4 className="text-lg font-semibold text-gray-900">{currentCert.label}</h4>
                                <span
                                    className={`text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider ${currentCert.required ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}
                                >
                                    {currentCert.required ? 'Required' : 'Optional'}
                                </span>
                            </div>
                            <p className="text-sm text-gray-500">Upload your certificate document</p>
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                        <i className="fas fa-exclamation-circle text-red-600 mt-0.5 mr-2"></i>
                        <p className="text-sm text-red-700">{error}</p>
                    </div>
                )}

                {/* Expiry Date Input */}
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Expiry Date <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="date"
                        className="block w-full border border-gray-300 rounded-lg shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        value={expiryDate}
                        onChange={(e) => {
                            setExpiryDate(e.target.value);
                            setError('');
                        }}
                        disabled={uploading}
                    />
                    {expiryDate && new Date(expiryDate) < new Date(new Date().setHours(0, 0, 0, 0)) && (
                        <p className="mt-1 text-xs text-amber-600 font-bold">
                            ⚠️ This document is expired. It will be flagged as "Action Required".
                        </p>
                    )}
                </div>

                {/* File Upload */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Document <span className="text-red-500">*</span>
                    </label>

                    {!selectedFile ? (
                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                <i className="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-2"></i>
                                <p className="text-sm text-gray-600">
                                    <span className="font-semibold">Click to upload</span> or drag and drop
                                </p>
                                <p className="text-xs text-gray-500 mt-1">PDF, JPG, PNG (max 10MB)</p>
                            </div>
                            <input
                                type="file"
                                className="hidden"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={handleFileSelect}
                                disabled={uploading}
                            />
                        </label>
                    ) : (
                        <div className="flex items-center justify-between p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                            <div className="flex items-center">
                                <i className="fas fa-file-pdf text-red-500 text-2xl mr-3"></i>
                                <div>
                                    <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                                    <p className="text-xs text-gray-500">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setSelectedFile(null)}
                                className="text-gray-400 hover:text-red-600"
                                disabled={uploading}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={handleSkip}
                        className="text-gray-600 hover:text-gray-900 font-medium text-sm"
                        disabled={uploading}
                    >
                        Skip for now
                    </button>

                    <div className="flex items-center space-x-3">
                        {currentStep > 0 && (
                            <button
                                type="button"
                                onClick={() => setCurrentStep(prev => prev - 1)}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                                disabled={uploading}
                            >
                                <i className="fas fa-arrow-left mr-2"></i>
                                Back
                            </button>
                        )}

                        <button
                            type="button"
                            onClick={handleUpload}
                            disabled={!selectedFile || !expiryDate || uploading}
                            className="px-6 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        >
                            {uploading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin mr-2"></i>
                                    Uploading...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-upload mr-2"></i>
                                    Upload {isLastCert ? '& Finish' : '& Next'}
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Skip All Option */}
                <div className="text-center mt-6 pt-4 border-t border-gray-100">
                    <button
                        type="button"
                        onClick={onFinish}
                        className="text-sm text-gray-500 hover:text-gray-700"
                        disabled={uploading}
                    >
                        Done →
                    </button>
                </div>
            </div>
        </div>
    );
}
